import axios from 'axios';
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Qualification levels
export const QUALIFICATION_LEVELS = [
  { value: 'diploma', label: 'Diploma', color: 'bg-blue-100 text-blue-700' },
  { value: 'bachelor', label: 'Bachelor Degree', color: 'bg-purple-100 text-purple-700' },
  { value: 'master', label: 'Master Degree', color: 'bg-amber-100 text-amber-700' },
  { value: 'doctoral', label: 'Doctoral Degree', color: 'bg-emerald-100 text-emerald-700' },
];

// Case statuses
export const CASE_STATUSES = [
  { value: 'pending_approval', label: 'Pending Approval', color: 'bg-amber-100 text-amber-700' },
  { value: 'application_rejected', label: 'Application Rejected', color: 'bg-red-100 text-red-700' },
  { value: 'draft', label: 'Draft', color: 'bg-slate-100 text-slate-700' },
  { value: 'documents_uploaded', label: 'Documents Uploaded', color: 'bg-blue-100 text-blue-700' },
  { value: 'ai_review', label: 'Credentia Review Complete', color: 'bg-purple-100 text-purple-700' },
  { value: 'assessor_review', label: 'Under Assessor Review', color: 'bg-amber-100 text-amber-700' },
  { value: 'iqa_review', label: 'Under IQA Review', color: 'bg-orange-100 text-orange-700' },
  { value: 'approved', label: 'Approved', color: 'bg-green-100 text-green-700' },
  { value: 'report_generated', label: 'Report Generated', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'closed', label: 'Closed', color: 'bg-gray-100 text-gray-700' },
];

// Document categories
export const DOCUMENT_CATEGORIES = [
  'Resume/CV',
  'Passport/IC Copy',
  'Proof of Address',
  'Academic Certificates',
  'Academic Transcripts',
  'Professional Certificates',
  'Awards and Recognition',
  'Employment Letters',
  'Job Description / Scope of Work',
  'Performance Reviews / Appraisals',
  'Recommendation Letters',
  'Employer Verification Letters',
  'Supporting Portfolio',
  'Research Publications',
  'Conference Participation Records',
  'Professional Body Membership',
  'Application Form',
  'Other Supporting Evidence',
];

// Score descriptions
export const SCORE_DESCRIPTIONS = [
  { score: 0, label: 'No Evidence', color: 'bg-red-500' },
  { score: 1, label: 'Minimal Evidence', color: 'bg-orange-500' },
  { score: 2, label: 'Partial Evidence', color: 'bg-amber-500' },
  { score: 3, label: 'Adequate Evidence', color: 'bg-yellow-500' },
  { score: 4, label: 'Strong Evidence', color: 'bg-lime-500' },
  { score: 5, label: 'Exceptional Evidence', color: 'bg-green-500' },
];

// Get status info
export const getStatusInfo = (status) => {
  return CASE_STATUSES.find(s => s.value === status) || CASE_STATUSES[0];
};

// Get level info
export const getLevelInfo = (level) => {
  return QUALIFICATION_LEVELS.find(l => l.value === level) || QUALIFICATION_LEVELS[0];
};

// Format date
export const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Format datetime
export const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Countries list for nationality dropdown
export const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", 
  "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", 
  "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", 
  "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cambodia", 
  "Cameroon", "Canada", "Cape Verde", "Central African Republic", "Chad", "Chile", 
  "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", 
  "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", 
  "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", 
  "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", 
  "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", 
  "Haiti", "Honduras", "Hong Kong", "Hungary", "Iceland", "India", "Indonesia", "Iran", 
  "Iraq", "Ireland", "Israel", "Italy", "Ivory Coast", "Jamaica", "Japan", "Jordan", 
  "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", 
  "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Macau", 
  "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", 
  "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", 
  "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", 
  "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", 
  "North Macedonia", "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama", 
  "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", 
  "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", 
  "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Saudi Arabia", "Senegal", 
  "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", 
  "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", 
  "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan", 
  "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", 
  "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", 
  "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", 
  "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Yemen", "Zambia", 
  "Zimbabwe"
];
